/**
 * Zeichnet den Fehler-Zähler der Benachrichtigungs-Glocke als rotes Badge auf
 * das Tab-Favicon (`public/favicon.svg`): Basis-Icon wird auf ein Canvas
 * gemalt, der Zähler überlagert und das Ergebnis als PNG-Data-URL in den
 * `<link rel="icon">` geschrieben. Bei Zähler 0 wird das Original wiederhergestellt.
 */

/** Canvas-Kantenlänge — 64 px reicht für alle Tab-Darstellungen, Browser skalieren herunter. */
const FAVICON_SIZE = 64;

/** Badge-Farbe — identisch zum Zähler-Badge an der Glocke (`bg-red-500`). */
const BADGE_COLOR = '#ef4444';

let originalHref: string | null = null;
let baseImagePromise: Promise<HTMLImageElement | null> | null = null;
let lastCount: number | null = null;

/** Kappt den Badge-Text wie an der Glocke — ab 100 wird `99+` angezeigt. */
export function formatFaviconBadgeCount(count: number): string {
  return count > 99 ? '99+' : String(count);
}

/**
 * Setzt das Favicon auf „Basis-Icon + roter Zähler" bzw. zurück aufs Original.
 * Idempotent pro Zähler-Stand; das Canvas-Rendern läuft asynchron (Bild-Load),
 * ein veralteter Render-Abschluss überschreibt keinen neueren Stand.
 */
export function updateFaviconBadge(count: number): void {
  if (typeof document === 'undefined') return;
  if (count === lastCount) return;
  lastCount = count;

  const link = getFaviconLink();
  originalHref ??= link.href;

  if (count <= 0) {
    link.href = originalHref;
    return;
  }

  void renderBadgedFavicon(count).then((dataUrl) => {
    // Nur anwenden, wenn zwischenzeitlich kein neuerer Zähler gerendert wurde.
    if (dataUrl !== null && lastCount === count) {
      getFaviconLink().href = dataUrl;
    }
  });
}

/** Findet den Icon-Link im Head — legt ihn an, falls (unerwartet) keiner existiert. */
function getFaviconLink(): HTMLLinkElement {
  const existing = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
  if (existing) return existing;

  const link = document.createElement('link');
  link.rel = 'icon';
  document.head.appendChild(link);
  return link;
}

/** Lädt das Basis-Icon einmalig — `null`, wenn es nicht ladbar ist (dann wird nur das Badge gezeichnet). */
function loadBaseImage(): Promise<HTMLImageElement | null> {
  baseImagePromise ??= new Promise((resolve) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => resolve(null);
    image.src = originalHref ?? '/favicon.svg';
  });
  return baseImagePromise;
}

/**
 * Rendert Basis-Icon + Badge als PNG-Data-URL — `null`, wenn kein
 * 2D-Canvas-Kontext verfügbar ist (dann bleibt das aktuelle Icon stehen).
 */
async function renderBadgedFavicon(count: number): Promise<string | null> {
  const canvas = document.createElement('canvas');
  canvas.width = FAVICON_SIZE;
  canvas.height = FAVICON_SIZE;
  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  const base = await loadBaseImage();
  if (base) {
    ctx.drawImage(base, 0, 0, FAVICON_SIZE, FAVICON_SIZE);
  }

  const label = formatFaviconBadgeCount(count);

  // Kreis für 1–2 Ziffern, Pille für „99+" — oben rechts, ragt leicht über den Rand.
  ctx.fillStyle = BADGE_COLOR;
  if (label.length <= 2) {
    ctx.beginPath();
    ctx.arc(46, 18, 17, 0, Math.PI * 2);
    ctx.fill();
  } else {
    const width = 40;
    ctx.beginPath();
    ctx.roundRect(FAVICON_SIZE - width - 2, 4, width, 28, 14);
    ctx.fill();
  }

  ctx.fillStyle = '#ffffff';
  ctx.font = `bold ${label.length <= 2 ? 22 : 17}px Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(label, label.length <= 2 ? 46 : FAVICON_SIZE - 22, label.length <= 2 ? 19 : 19);

  return canvas.toDataURL('image/png');
}
